// src/utils/sessionTracker.js
import { db } from "../../firebase/firebase.jsx";
import {
  doc,
  setDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";

const SESSION_STORAGE_KEY = "bd_session_id";

/**
 * Get or create a stable session id for THIS browser/device.
 */
function getOrCreateSessionId() {
  let id = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!id) {
    id =
      "sess_" +
      Math.random().toString(36).slice(2) +
      Date.now().toString(36);
    localStorage.setItem(SESSION_STORAGE_KEY, id);
  }
  return id;
}

/**
 * Basic device info from the browser.
 */
function getDeviceInfo() {
  if (typeof navigator === "undefined") {
    return {
      deviceName: "Unknown device",
      browser: "Unknown",
      platform: "Unknown",
      userAgent: "Unknown",
    };
  }

  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "Unknown";

  let browser = "Unknown";
  if (/Chrome/i.test(ua) && !/Edge|Edg/i.test(ua)) browser = "Chrome";
  else if (/Edg/i.test(ua)) browser = "Edge";
  else if (/Firefox/i.test(ua)) browser = "Firefox";
  else if (/Safari/i.test(ua)) browser = "Safari";

  return {
    deviceName: "This device",
    browser,
    platform,
    userAgent: ua,
  };
}

/**
 * Create or update THIS browser's session document for the given user.
 * Call this after login / when AuthContext sees a logged-in user.
 */
export async function registerSessionForUser(user) {
  if (!user) return;

  const sessionId = getOrCreateSessionId();
  const info = getDeviceInfo();

  const ref = doc(db, "sessions", sessionId);
  await setDoc(
    ref,
    {
      uid: user.uid,
      email: user.email || null,
      ...info,
      lastActive: serverTimestamp(),
      current: true,
      forceLogout: false,
    },
    { merge: true }
  );
}

/**
 * Watch THIS session doc. If forceLogout becomes true, call onForceLogout().
 * Returns an unsubscribe function.
 */
export function watchCurrentSession(user, onForceLogout) {
  if (!user) return () => {};

  const sessionId = getOrCreateSessionId();
  const ref = doc(db, "sessions", sessionId);

  return onSnapshot(ref, (snap) => {
    const data = snap.data();
    if (!data) return;
    if (data.forceLogout && typeof onForceLogout === "function") {
      onForceLogout();
    }
  });
}

/**
 * Mark THIS session as signed out on this device.
 * Call this when the user signs out on this browser.
 */
export async function markSessionSignedOut() {
  const sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!sessionId) return;

  const ref = doc(db, "sessions", sessionId);
  await setDoc(
    ref,
    {
      current: false,
      lastActive: serverTimestamp(),
    },
    { merge: true }
  );
}
